import type { CreateCharacterPayload } from '@proj-airi/stage-ui/types/character'

import { useCharacterStore } from '@proj-airi/stage-ui/stores/characters'

const modelAssetsBaseUrl = (import.meta.env.VITE_MODEL_ASSETS_BASE_URL || '')
  .trim()
  .replace(/^VITE_MODEL_ASSETS_BASE_URL\s*=\s*/, '')
  .replace(/\/+$/, '')

function getAyakaUrl(path: string, type: 'live2d' | 'vrm' = 'live2d') {
  if (modelAssetsBaseUrl) {
    if (type === 'live2d') {
      // User's R2 stores Live2D as .zip files
      const zipName = path.split('/')[1] // e.g. "hiyori_pro_zh" from "live2d-models/hiyori_pro_zh/..."
      return `${modelAssetsBaseUrl}/live2d-models/${zipName}.zip`
    }
    return `${modelAssetsBaseUrl}/${path}`
  }
  return `https://dist.ayaka.moe/${path}`
}

const PRESET_CHARACTERS = [
  {
    id: 'hiyori-pro',
    name: 'Hiyori Pro (Live2D)',
    description: 'A premium Live2D preset character.',
    type: 'live2d' as const,
    modelUrl: getAyakaUrl('live2d-models/hiyori_pro_zh/hiyori_pro_zh.model3.json', 'live2d'),
  },
  {
    id: 'hiyori-free',
    name: 'Hiyori Free (Live2D)',
    description: 'A free Live2D preset character.',
    type: 'live2d' as const,
    modelUrl: getAyakaUrl('live2d-models/hiyori_free_zh/hiyori_free_zh.model3.json', 'live2d'),
  },
  {
    id: 'vroid-a',
    name: 'Avatar Sample A (VRM)',
    description: 'VRoid Studio default female avatar sample A.',
    type: 'vrm' as const,
    modelUrl: getAyakaUrl('vrm-models/VRoid-Hub/AvatarSample-A/AvatarSample_A.vrm', 'vrm'),
  },
  {
    id: 'vroid-b',
    name: 'Avatar Sample B (VRM)',
    description: 'VRoid Studio default female avatar sample B.',
    type: 'vrm' as const,
    modelUrl: getAyakaUrl('vrm-models/VRoid-Hub/AvatarSample-B/AvatarSample_B.vrm', 'vrm'),
  },
  {
    id: 'vroid-c',
    name: 'Avatar Sample C (VRM)',
    description: 'VRoid Studio default male avatar sample C.',
    type: 'vrm' as const,
    modelUrl: getAyakaUrl('vrm-models/VRoid-Hub/AvatarSample-C/AvatarSample_C.vrm', 'vrm'),
  },
]

export async function seedPresetCharacters() {
  const store = useCharacterStore()
  const existingChars = Array.from(store.characters.values())
  // Use dynamic import to avoid circular dependencies if any, or just import it at top.
  const { charactersRepo } = await import('@proj-airi/stage-ui/database/repos/characters.repo')
  const { nanoid } = await import('nanoid')

  for (const preset of PRESET_CHARACTERS) {
    const existing = existingChars.find(c => c.characterId === preset.id)
    if (existing) {
      const isVrm = preset.type === 'vrm'
      // Only Live2d URLs are deeply nested, VRM is vrm.urls
      const currentUrl = isVrm
        ? existing.avatarModels?.[0]?.config?.vrm?.urls?.[0]
        : existing.avatarModels?.[0]?.config?.live2d?.urls?.[0]

      if (!existing.avatarModels?.length || currentUrl !== preset.modelUrl) {
        if (!existing.avatarModels?.length) {
          existing.avatarModels = [{
            id: nanoid(),
            characterId: existing.id,
            name: preset.name,
            type: preset.type,
            description: 'Default preset model',
            config: isVrm ? { vrm: { urls: [preset.modelUrl] } } : { live2d: { urls: [preset.modelUrl] } },
            createdAt: new Date(),
            updatedAt: new Date(),
          }]
        }
        else {
          existing.avatarModels[0].config = isVrm ? { vrm: { urls: [preset.modelUrl] } } : { live2d: { urls: [preset.modelUrl] } }
        }
        await charactersRepo.upsert(existing)
        store.characters.set(existing.id, existing)
        console.log(`Updated preset ${preset.id} model url to ${preset.modelUrl}`)
      }
      continue
    }

    const payload: CreateCharacterPayload = {
      character: {
        characterId: preset.id,
        version: '1.0.0',
        coverUrl: '',
      },
      i18n: [{
        language: 'en',
        name: preset.name,
        description: preset.description,
        tags: ['preset', preset.type],
      }],
      avatarModels: [{
        name: preset.name,
        type: preset.type,
        description: 'Default preset model',
        config: preset.type === 'vrm'
          ? { vrm: { urls: [preset.modelUrl] } }
          : { live2d: { urls: [preset.modelUrl] } },
      }],
      capabilities: [
        {
          type: 'llm',
          config: {
            apiKey: '',
            apiBaseUrl: '',
            llm: { model: 'gpt-4o-mini', temperature: 0.7 },
          },
        },
        {
          type: 'tts',
          config: {
            apiKey: '',
            apiBaseUrl: '',
            tts: { voiceId: 'alloy', speed: 1.0, ssml: '', pitch: 1.0 },
          },
        },
      ],
      prompts: [
        { language: 'en', type: 'system', content: 'You are a helpful AI assistant residing in a digital space. Keep your answers concise.' },
        { language: 'en', type: 'personality', content: 'You are friendly, polite, and slightly playful.' },
        { language: 'en', type: 'greetings', content: 'Hello! I am ready to help you.' },
      ],
    }

    try {
      await store.create(payload)
    }
    catch (err) {
      console.error(`Failed to seed preset ${preset.id}:`, err)
    }
  }
}
